import React, { useEffect, useState, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import styled from 'styled-components'
import DOMPurify from 'dompurify'

import { IoCloseOutline } from 'react-icons/io5'
import { useToasts } from 'react-toast-notifications'

import Tabs from '../Tabs'
import Tags from '../Tags'
import UploadedCV from './components/UploadedCV'
import ManualCV from './components/ManualCV'
import WFHSection from './components/WFHSection'
import SpeedtestSection from './components/SpeedtestSection'
import RemoteWorkEthicsSection from './components/RemoteWorkEthicsSection'
import ApplicantScorecardSection from './components/ApplicantScorecardSection'
import DashboardButton from '../dashboard/DashboardButton'
import Flex from '../layouts/Flex'

import { getTalent } from '../../store/actions/talentActions'
import { setModal } from '../../store/actions/listsActions'

import useFetch from '../../hooks/useFetch'
import useParseCV from '../../hooks/useParseCV'

import { markText } from '../../utils/utils'

const CVDetails = ({
  primaryIcon,
  closeEvent = null,
  isCVLoaded,
  downloadRef = null,
  showWFH = true,
  showBackupInfrastructure = true,
  showRemoteFitness = true,
  showSpeedTest = true,
  showScorecard = true,
}) => {
  const { addToast } = useToasts()

  const { uploadedCv, globalSearchTerm, talent, scorecard } = useSelector(
    (state) => state.talents
  )
  const { modal } = useSelector((state) => state.lists)

  const dispatch = useDispatch()

  const [professionalHeadline, setProfessionalHeadline] = useState('')

  const meta = talent && talent.meta ? JSON.parse(talent.meta) : null
  const experienceState = (meta && meta.experienceState) || null
  const skills = meta?.fullSkills || []
  const technicalSkills = skills[0] || []
  const characterReference =
    talent && talent.character_reference
      ? JSON.parse(talent.character_reference)
      : []

  const { activeTab } = useSelector((state) => state.tabs)

  const { data: remoteData } = useFetch(`user/${talent.user_id}/remote-work`)
  const { loading, parseCV } = useParseCV()

  const remoteDataResp = remoteData && remoteData?.resp

  const compSpec =
    remoteDataResp && remoteData?.resp?.computer_specification !== ''
      ? JSON.parse(remoteData?.resp?.computer_specification)
      : null

  const pheripheral =
    remoteDataResp && remoteData?.resp?.peripherals !== ''
      ? JSON.parse(remoteData?.resp?.peripherals)
      : null

  const ethics =
    remoteDataResp && remoteData?.resp?.meta !== ''
      ? JSON.parse(remoteData?.resp?.meta)
      : null

  const internetSpeedTest =
    remoteDataResp && remoteData?.resp?.internet_speed_test !== ''
      ? JSON.parse(remoteData?.resp?.internet_speed_test)
      : null

  const [backupInfra, setBackupInfrastructure] = useState(null)

  const currentUser = localStorage.getItem('user')
  const role = JSON.parse(currentUser)?.user_type

  useEffect(() => {
    if (remoteDataResp?.backup_infrastructure) {
      try {
        setBackupInfrastructure(
          JSON.parse(remoteDataResp.backup_infrastructure)
        )
      } catch (e) {
        setBackupInfrastructure(null)
      }
    }
  }, [remoteDataResp?.backup_infrastructure])

  const handleParseCV = async () => {
    if (loading && !isCVLoaded) return

    try {
      await parseCV(uploadedCv, talent?.user_id)

      dispatch(getTalent(talent?.id))

      addToast('CV parsed successfully', {
        appearance: 'success',
      })
    } catch (error) {
      addToast(error?.message, {
        appearance: 'error',
      })
    }
  }

  const handleEditApplicantDetails = () => {
    dispatch(setModal([...modal, 'editApplicantDetails']))
  }

  useEffect(() => {
    const lists = []

    talent &&
      talent.professional_headline &&
      JSON.parse(talent.professional_headline) &&
      JSON.parse(talent.professional_headline).length > 0 &&
      JSON.parse(talent.professional_headline).forEach(({ item }) => {
        lists.push(item)
      })
    setProfessionalHeadline(lists.splice(' • ').join(' • '))
  }, [])

  const markAndSanitize = (str) => {
    return DOMPurify.sanitize(markText(str, globalSearchTerm))
  }

  return (
    <StyledCVDetails>
      <StyledHeader>
        <Tabs
          margin="1rem 0 0.75rem 0.75rem"
          tabList={[
            { name: 'Uploaded CV', value: 'uploadedCV' },
            { name: 'HOPLA Template', value: 'hoplaTemplate' },
          ]}
          tabGroup="applicantCV"
        />
        <IconList>
          {primaryIcon}
          {closeEvent !== null && <IoCloseOutline onClick={closeEvent} />}
        </IconList>
      </StyledHeader>

      {activeTab?.applicantCV === 'hoplaTemplate' && downloadRef === null && (
        <StyledSectionWrapper>
          <Flex items="center" justify="space-between">
            {talent?.is_parsed ? (
              <Tags
                text="HOPLA Generated CV"
                status="active"
                marginBottom="1rem"
              />
            ) : (
              role !== 'sales' && (
                <DashboardButton
                  color="#1877f2"
                  onClick={handleParseCV}
                  type="submit"
                  text={loading ? 'Generating CV...' : 'Generate HOPLA CV'}
                  notIcon
                  maxWidth="200px"
                  marginBottom="1rem"
                />
              )
            )}
            {role !== 'sales' && (
              <DashboardButton
                onClick={handleEditApplicantDetails}
                type="submit"
                text="Edit Applicant Details"
                notIcon
                maxWidth="200px"
              />
            )}
          </Flex>
        </StyledSectionWrapper>
      )}

      <div ref={downloadRef}>
        <StyledSectionWrapper>
          {activeTab?.applicantCV === 'uploadedCV' &&
            uploadedCv &&
            uploadedCv.public_id && (
              <UploadedCV
                pages={[...Array(uploadedCv?.pages).keys()]}
                url={uploadedCv?.url}
              />
            )}

          {activeTab?.applicantCV === 'hoplaTemplate' && (
            <>
              <ManualCV
                name={talent?.name}
                professionalHeadline={professionalHeadline}
                ooklaLink={internetSpeedTest?.ooklaLink}
                email={talent?.email}
                phoneNumber={talent?.phone_number}
                professionalSummary={talent?.professional_summary}
                workExperience={
                  experienceState && experienceState.length > 0
                    ? experienceState.map((exp) => ({
                        ...exp,
                        description: markAndSanitize(exp.description),
                      }))
                    : []
                }
                technicalSkills={technicalSkills}
                characterReferences={characterReference}
              />
              <hr />
            </>
          )}

          {showWFH && (
            <>
              <WFHSection
                os={compSpec?.os}
                processor={compSpec?.processor}
                ram={compSpec?.ram}
                osVersion={compSpec?.osVersion}
                webCamera={pheripheral?.webCamera}
                headSet={pheripheral?.headSet}
                microphone={pheripheral?.microphone}
                downloadSpeed={internetSpeedTest?.downloadSpeed}
                uploadSpeed={internetSpeedTest?.uploadSpeed}
                pingTime={internetSpeedTest?.pingTime}
                speedTestImageUrl={remoteDataResp?.internet_speed_image_url}
                backupInfrastructure={backupInfra}
                showBackupInfrastructure={showBackupInfrastructure}
              />
              <hr />
            </>
          )}

          {showRemoteFitness && (
            <>
              <RemoteWorkEthicsSection
                workStation={ethics?.workStation}
                device={ethics?.device}
                workSchedule={ethics?.workSchedule}
                workShift={ethics?.workShift}
                timeTracker={ethics?.timeTracker}
              />
              <hr />
            </>
          )}

          {showSpeedTest && (
            <>
              <SpeedtestSection
                speedTestImageUrl={remoteDataResp?.internet_speed_image_url}
              />
              <hr />
            </>
          )}

          {showScorecard && (
            <ApplicantScorecardSection
              interviews={scorecard?.interviews}
              totalWeightedScore={scorecard?.totalWeightedScore}
              overallRating={scorecard?.overallRating}
            />
          )}
        </StyledSectionWrapper>
      </div>
    </StyledCVDetails>
  )
}

export default memo(CVDetails)

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.75rem;
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fff;
`

const IconList = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 1.5rem;
  align-items: center;

  svg {
    cursor: pointer;
  }
`

const StyledCVDetails = styled.div`
  height: calc(-1rem + 74vh);
  overflow-y: auto;
`

const StyledSectionWrapper = styled.div`
  padding: 0 1rem 1rem 1rem;
`
